import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CSS, Bench, Ltr, nextStop, tripDay, tripDayText, friendlyDate, feedLine, ESIM_PLAN,
  TripSection, PlanSection, BookingsSection, ESimSection, TravelNumberSection, TranslateSection, HotelSection, LocalSection, ReadySection, FoodSection,
  PhoneLog, TripPlaces, mmss, useP3Phone, postJson, requestJson, api, Field, MiloCallsSection, NotificationBell, TripUpdate, setJumpOverride, useBenchState, useMiloChat, ChatThread, ChatComposer, chatEvents,
  isConfirmedBooking,
} from './P3TestBench';
import { t, dir, formatDate, formatDateTime, formatNumber } from '../i18n';
import { LANGUAGES } from '../i18n/languages';
import { SMS_CONSENT } from './P3Signup';
import { PayProvider, useBilling, usePay, CardPicker, cents, payReady } from './P3Pay';

/*
 * P3 Travel Pass, production layout (#/p3): Today / Milo / Trips / My, full screen on phones and a
 * phone-shaped column on larger screens. Same trip state, backend and tools as the test bench;
 * Milo's chat is the way into all of them: it answers, places calls, runs searches, shows routes,
 * or opens a tool in a sheet. Signed in (My → account), trips, the phone log and Milo's history are saved
 * to the account; guests keep everything in this browser.
 */

// Tools that open in a bottom sheet (the itinerary is on the Trips tab, the phone and call/text logs on Call/SMS).
const SHEETS = {
  trip: ['pa.profile', ({ app }) => <TripSection state={app.state} update={app.update} sampleOpen={app.sampleOpen} setSampleOpen={app.setSampleOpen} />],
  products: ['pa.getMore', () => <Products />],
  account: ['pa.acct.title', ({ app }) => <AccountSheet app={app} />],
  credits: ['pa.buyCredits', ({ app }) => <BuyCredits app={app} />],
  bookings: ['title.bookings', ({ app }) => <BookingsSection ctx={app.ctx} state={app.state} update={app.update} />],
  esim: ['title.esim', ({ app }) => <ESimSection state={app.state} update={app.update} />],
  number: ['title.number', ({ app }) => <TravelNumberSection state={app.state} update={app.update} />],
  translate: ['title.translate', ({ app }) => <TranslateSection ctx={app.ctx} />],
  hotel: ['title.hotel', ({ app }) => <HotelSection ctx={app.ctx} state={app.state} />],
  local: ['title.local', ({ app }) => <LocalSection ctx={app.ctx} state={app.state} />],
  ready: ['title.ready', ({ app }) => <ReadySection ctx={app.ctx} />],
  food: ['title.food', ({ app }) => <FoodSection ctx={app.ctx} />],
};

/** Line icons for the tab bar (24px, current colour). */
const Icon = ({ name }) => {
  const p = {
    today: <><circle cx="12" cy="12" r="4.2" /><path d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M5.5 18.5l1.7-1.7M16.8 7.2l1.7-1.7" /></>,
    milo: <path d="M4.5 6.5a2.5 2.5 0 0 1 2.5-2.5h10a2.5 2.5 0 0 1 2.5 2.5v7.5a2.5 2.5 0 0 1-2.5 2.5h-6l-4.5 3.5v-3.5h-.5A1.5 1.5 0 0 1 4.5 15z" />,
    trips: <><rect x="3.5" y="7" width="17" height="12.5" rx="2.5" /><path d="M9 7V5.2A1.2 1.2 0 0 1 10.2 4h3.6A1.2 1.2 0 0 1 15 5.2V7M3.5 12.5h17" /></>,
    phone: <path d="M8.2 3.8 6 4.4c-1 .3-1.7 1.3-1.5 2.4.9 6.3 6.4 11.8 12.7 12.7 1.1.2 2.1-.5 2.4-1.5l.6-2.2a1 1 0 0 0-.6-1.2l-3-1.2a1 1 0 0 0-1.1.3l-1.2 1.4a11 11 0 0 1-5.1-5.1l1.4-1.2a1 1 0 0 0 .3-1.1l-1.2-3a1 1 0 0 0-1.2-.6z" />,
    my: <><circle cx="12" cy="8.5" r="3.8" /><path d="M4.5 20c.9-3.8 4-5.8 7.5-5.8s6.6 2 7.5 5.8" /></>,
  }[name];
  return <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{p}</svg>;
};
const TABS = ['today', 'milo', 'phone', 'trips', 'my'];
const Chevron = () => <span className="pa-chev" aria-hidden="true">›</span>;

const PA_CSS = `
.pa{ --pa-bg:#f5f6f8; --pa-muted:#5f6c82; min-height:100dvh; background:#dfe3e9; font-size:17px; }
.pa-wrap{ position:relative; width:100%; height:100dvh; margin:0 auto; display:flex; flex-direction:column; background:var(--pa-bg); overflow:hidden; }
@media (min-width:600px){
  .pa{ padding:16px 0; }
  .pa-wrap{ width:min(760px, 100% - 32px); height:calc(100dvh - 32px); border-radius:32px; box-shadow:0 30px 80px rgba(23,38,61,.22); }
}
.pa .tb-note{ font-size:15px; color:var(--pa-muted); }
.pa-screen{ flex:1; min-height:0; overflow-y:auto; overflow-x:hidden; -webkit-overflow-scrolling:touch; overscroll-behavior:contain; }
.pa-pad{ padding:18px 18px 28px; display:grid; gap:18px; }
.pa h1,.pa h2,.pa h3{ color:var(--ink); text-transform:none; letter-spacing:-.01em; }
.pa-big{ font-size:30px; font-weight:800; line-height:1.12; margin:0; }
.pa-h{ font-size:20px; font-weight:750; margin:0; }

/* tab bar */
.pa-nav{ flex:0 0 auto; display:grid; grid-template-columns:repeat(5,1fr); padding:6px 6px calc(8px + env(safe-area-inset-bottom)); background:#fff; border-top:1px solid var(--line); }
.pa-nav button{ all:unset; cursor:pointer; display:grid; justify-items:center; align-content:center; gap:3px; min-height:58px; border-radius:14px; font-size:12.5px; font-weight:650; color:#8a93a3; }
.pa-nav button[aria-current="page"]{ color:var(--navy); }
.pa-nav button[aria-current="page"] svg{ color:var(--gold); }
.pa-nav button:focus-visible{ outline:2px solid var(--gold); outline-offset:-2px; }
.pa-nav button{ position:relative; }
.pa-live{ position:absolute; top:8px; right:calc(50% - 18px); width:9px; height:9px; border-radius:50%; background:#12b76a; box-shadow:0 0 0 2px #fff; }

/* Today: the photo, the trip strip, and Milo's box on the photo's edge */
.pa-hero{ position:relative; height:clamp(300px, 46dvh, 430px); padding:18px 20px 64px; color:#fff; display:flex; flex-direction:column; isolation:isolate; }
.pa-hero img{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; z-index:-2; }
.pa-hero::before{ content:""; position:absolute; inset:0; z-index:-1; background:linear-gradient(180deg,rgba(23,38,61,.1) 0%,rgba(23,38,61,.0) 30%,rgba(23,38,61,.82) 100%); }
.pa-hero-top{ display:flex; align-items:center; gap:10px; }
.pa-place{ display:inline-flex; align-items:center; gap:6px; min-height:40px; padding:0 14px; border-radius:20px; background:rgba(255,255,255,.92); color:var(--ink); font-weight:700; font-size:15px; max-width:70%; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.pa-hero .tb-bellwrap{ margin-inline-start:auto; }
.pa-hero .tb-bell{ width:44px; height:44px; background:rgba(255,255,255,.92); color:var(--ink); }
.pa-hero .tb-bellpop{ color:var(--ink); text-shadow:none; }
.pa-hero:has(.tb-bellpop){ z-index:3; }
.pa-hero h1{ margin:auto 0 4px; font-size:34px; font-weight:800; line-height:1.08; color:#fff; text-shadow:0 2px 16px rgba(0,0,0,.25); }
.pa-hero p{ margin:0; font-size:16px; opacity:.92; }
.pa-strip{ display:flex; gap:4px; margin-top:14px; }
.pa-strip i{ flex:1; height:6px; border-radius:3px; background:rgba(255,255,255,.32); }
.pa-strip i.done{ background:rgba(255,233,195,.75); }
.pa-strip i.now{ background:var(--gold); box-shadow:0 0 0 2px rgba(255,233,195,.55); }
.pa-askwrap{ position:relative; z-index:2; margin:-34px 18px 0; }
.pa-ask{ display:flex; align-items:center; gap:10px; padding:8px 8px 8px 16px; min-height:64px; border-radius:22px; background:#fff; box-shadow:0 12px 32px rgba(23,38,61,.16); }
.pa-ask .tb-avatar{ width:40px; height:40px; font-size:22px; animation:none; flex:0 0 40px; }
.pa-ask input{ flex:1; min-width:0; border:0; background:transparent; font:inherit; font-size:17px; color:var(--ink); padding:10px 0; }
.pa-ask input:focus{ outline:none; }
.pa-ask button{ flex:0 0 48px; height:48px; border-radius:16px; border:0; background:var(--navy); color:var(--cream); font-size:19px; cursor:pointer; }

.pa-status{ display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.pa-stat{ all:unset; box-sizing:border-box; cursor:pointer; display:grid; gap:4px; padding:16px; border-radius:20px; background:#fff; border:1px solid var(--line); min-height:112px; align-content:start; }
.pa-stat small{ color:var(--pa-muted); font-size:14px; }
.pa-stat strong{ font-size:21px; font-weight:800; line-height:1.15; }
.pa-stat .pa-meter{ margin-top:6px; }
.pa-stat:focus-visible{ outline:2px solid var(--gold); }
.pa-meter{ height:8px; border-radius:4px; background:#e8ebf0; overflow:hidden; }
.pa-meter i{ display:block; height:100%; border-radius:4px; background:var(--gold); }

.pa-quick{ display:grid; grid-template-columns:repeat(4,1fr); gap:8px; }
.pa-quick button{ all:unset; cursor:pointer; display:grid; justify-items:center; gap:8px; text-align:center; font-size:13.5px; font-weight:650; color:var(--ink); line-height:1.2; padding:4px 0; border-radius:16px; }
.pa-quick button span{ display:grid; place-items:center; width:60px; height:60px; border-radius:20px; background:#fff; border:1px solid var(--line); font-size:26px; }
.pa-quick button:focus-visible{ outline:2px solid var(--gold); }
.pa-head{ display:flex; justify-content:space-between; align-items:baseline; gap:12px; }
.pa-head button{ all:unset; cursor:pointer; color:var(--gold-ink); font-weight:650; font-size:15px; padding:6px 0; }
.pa-days{ display:flex; gap:12px; overflow-x:auto; margin:0 -18px; padding:2px 18px 6px; scroll-snap-type:x mandatory; scroll-padding-inline:18px; scrollbar-width:none; }
.pa-day{ flex:0 0 72%; max-width:260px; scroll-snap-align:start; background:#fff; border:1px solid var(--line); border-radius:22px; overflow:hidden; display:flex; flex-direction:column; }
.pa-day img{ width:100%; height:120px; object-fit:cover; }
.pa-day > div{ padding:14px; display:grid; gap:4px; flex:1; }
.pa-day small{ color:var(--pa-muted); font-size:14px; }
.pa-day b{ font-size:18px; line-height:1.25; }
.pa-day .tb-ghost{ margin-top:8px; min-height:44px; justify-self:start; }

/* Milo */
.pa-milo{ flex:1; min-height:0; display:flex; flex-direction:column; }
.pa-bar{ display:flex; align-items:center; gap:12px; padding:14px 18px; min-height:68px; background:var(--pa-bg); border-bottom:1px solid var(--line); }
.pa-bar h2{ margin:0; font-size:20px; font-weight:800; }
.pa-bar .tb-avatar{ animation:none; }
.pa-bar .tb-bellwrap{ margin-inline-start:auto; }
.pa-bar .tb-bell{ width:44px; height:44px; }
.pa-milo .tb-chat{ max-height:none; flex:1; min-height:0; overflow-y:auto; padding:16px 18px; margin:0; align-content:start; gap:12px; }
.pa .tb-bubble{ font-size:17px; padding:12px 16px; border-radius:22px; }
.pa-hello{ font-size:20px; font-weight:600; line-height:1.4; margin:0; color:var(--ink); }
.pa-rows{ display:grid; background:#fff; border:1px solid var(--line); border-radius:20px; overflow:hidden; }
.pa-account{ gap:10px; }
.pa-acct-btns{ display:flex; gap:10px; flex-wrap:wrap; margin-top:4px; }
.pa-acct-btns a{ display:inline-flex; align-items:center; text-decoration:none; }
.pa-acct{ display:grid; gap:14px; }
.pa-acct form{ display:grid; gap:10px; }
.pa-acct .pa-h{ font-size:17px; margin-top:8px; }
.pa-acct-check{ display:flex; gap:10px; align-items:flex-start; font-size:14px; line-height:1.5; color:var(--pa-muted); cursor:pointer; }
.pa-acct-check input{ width:20px; height:20px; margin-top:2px; flex:0 0 20px; accent-color:var(--navy); }
.pa-acct-danger .pa-h{ color:#b42318; }
.pa-trip-open{ all:unset; cursor:pointer; flex:1; min-width:0; }
.pa-trip-open:focus-visible{ outline:2px solid var(--gold); outline-offset:2px; border-radius:6px; }
.pa-x{ all:unset; cursor:pointer; width:36px; height:36px; display:grid; place-items:center; border-radius:10px; color:var(--pa-muted); flex:0 0 36px; }
.pa-x:hover{ background:#fef3f2; color:#b42318; }
.pa-x:focus-visible{ outline:2px solid var(--gold); }
.pa-row{ all:unset; box-sizing:border-box; cursor:pointer; display:flex; align-items:center; gap:12px; min-height:56px; padding:12px 16px; font-size:16.5px; color:var(--ink); border-bottom:1px solid #eef0f4; }
.pa-row:last-child{ border-bottom:0; }
.pa-row:hover{ background:#fbf8f2; }
.pa-row:focus-visible{ outline:2px solid var(--gold); outline-offset:-2px; }
.pa-row > span:first-child{ flex:1; min-width:0; }
.pa-row b{ display:block; font-weight:650; }
.pa-row small{ display:block; color:var(--pa-muted); font-size:14px; margin-top:1px; }
.pa-chev{ color:#a6aebb; font-size:24px; line-height:1; }
.pa-composer{ padding:10px 14px calc(10px + env(safe-area-inset-bottom)); background:var(--pa-bg); border-top:1px solid var(--line); display:grid; gap:8px; }
.pa-attach{ display:flex; gap:8px; overflow-x:auto; scrollbar-width:none; }
.pa-attach .tb-chip{ flex:0 0 auto; min-height:40px; font-size:15px; padding:0 14px; }
.pa .tb-composer{ border-radius:24px; padding:6px; }
.pa .tb-composer textarea{ font-size:17px; padding:10px 12px; }
.pa .tb-round{ width:48px; height:48px; min-height:48px; flex-basis:48px; border-radius:16px; }

/* Trips & My */
.pa-title{ padding:20px 18px 6px; }
.pa-title .tb-note{ display:block; margin-top:4px; }
.pa-seg{ display:flex; gap:4px; margin:10px 18px 0; padding:4px; border-radius:16px; background:#e8ebf0; }
.pa-seg button{ all:unset; cursor:pointer; flex:1; min-width:0; text-align:center; min-height:42px; line-height:42px; padding:0 6px; border-radius:12px; font-size:14.5px; font-weight:650; color:var(--pa-muted); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.pa-seg button[aria-selected="true"]{ background:#fff; color:var(--ink); box-shadow:0 1px 4px rgba(23,38,61,.12); }
.pa-seg button:focus-visible{ outline:2px solid var(--gold); }
.pa-pass{ all:unset; box-sizing:border-box; cursor:pointer; display:grid; gap:6px; padding:20px; border-radius:24px; color:#fff; background:var(--navy); }
.pa-pass .pa-big{ color:#fff; font-size:26px; }
.pa-pass span{ font-size:15px; color:rgba(255,255,255,.78); }
.pa-pass .pa-strip i{ background:rgba(255,255,255,.18); }
.pa-pass .pa-strip i.done{ background:rgba(255,233,195,.55); }
.pa-tag{ font-size:13px; padding:5px 10px; border-radius:999px; background:var(--ok-bg); color:var(--ok); font-weight:750; white-space:nowrap; }
.pa-tag.mid{ background:var(--mid-bg); color:var(--mid); }
.pa-rows .pa-row.off{ opacity:.55; text-decoration:line-through; }
.pa-row.pa-danger b{ color:#b42318; }
.pa-row.pa-danger:hover{ background:#fef3f2; }
.pa-rows select{ font:inherit; font-size:16px; border:1px solid var(--line); border-radius:12px; padding:8px 10px; background:#fff; color:var(--ink); }
.pa-tools{ grid-template-columns:repeat(4,1fr); row-gap:16px; }
.pa-title-row{ display:flex; align-items:center; justify-content:space-between; gap:12px; }
.pa-title-row .tb-chip{ min-height:40px; font-size:14px; white-space:nowrap; }
.pa-product{ cursor:default; }
.pa-pass .pa-tag{ color:var(--ok); }
.pa-credits{ display:flex; align-items:center; justify-content:space-between; gap:12px; margin:10px 0 2px; padding:12px 14px; border-radius:16px; background:rgba(255,255,255,.08); }
.pa-credits small{ display:block; font-size:13px; color:rgba(255,255,255,.7); }
.pa-credits strong{ display:block; font-size:24px; font-weight:800; color:#fff; }
.pa-credits .tb-gold{ min-height:44px; }
.pa-li-big{ display:flex; align-items:baseline; justify-content:space-between; padding:14px 16px; border-radius:16px; background:#fff; border:1px solid var(--line); }
.pa-li-big strong{ font-size:26px; font-weight:800; }
.pa-product-head{ display:flex; align-items:center; justify-content:space-between; gap:10px; }
.pa-includes{ margin:8px 0 0; padding:0; list-style:none; display:grid; gap:6px; }
.pa-includes li{ display:flex; gap:8px; align-items:baseline; font-size:15px; color:rgba(255,255,255,.9); }
.pa-includes li::before{ content:"✓"; color:var(--cream); font-weight:800; }
.pa-prod-icon{ flex:0 0 auto !important; font-size:24px; }

/* sheets */
.pa-sheet-bg{ position:absolute; inset:0; z-index:30; background:rgba(8,21,41,.45); display:flex; align-items:flex-end; animation:tbFade .2s ease; }
.pa-sheet{ width:100%; height:92%; overflow-y:auto; background:var(--pa-bg); border-radius:28px 28px 0 0; padding:8px 14px calc(24px + env(safe-area-inset-bottom)); animation:tbUp .28s ease both; }
.pa-grab{ width:44px; height:5px; border-radius:3px; background:#cfd5de; margin:4px auto 8px; }
.pa-sheet-head{ display:flex; align-items:center; justify-content:space-between; gap:10px; margin:0 4px 12px; }
.pa-sheet-head h3{ margin:0; font-size:22px; font-weight:800; }
.pa-x{ width:44px; height:44px; border-radius:14px; border:0; background:#fff; font-size:22px; cursor:pointer; color:var(--ink); }
.pa-x:focus-visible{ outline:2px solid var(--gold); }
/* shared tool sections: the sheet or tab already names them */
.pa .tb-card > header .tb-kicker, .pa .tb-card > header h3{ display:none; }
.pa .tb-card > header p{ font-size:15px; }
.pa-sheet .tb-card, .pa-pad > .tb-card{ border:0; background:transparent; padding:0 4px; }
.pa-phone{ display:grid; justify-items:center; gap:6px; padding:18px 16px 20px; border-radius:28px; background:#fff; box-shadow:0 2px 14px rgba(23,38,61,.07); }
.pa-phone-status{ display:flex; align-items:center; gap:8px; font-size:13px; color:#5b6474; text-align:center; }
.pa-dial{ all:unset; box-sizing:border-box; width:100%; text-align:center; font-size:30px; font-weight:600; letter-spacing:.5px; color:var(--ink); padding:6px 0 0; font-variant-numeric:tabular-nums; }
.pa-dial::placeholder{ color:#c3c9d3; }
.pa-dial-label{ font-size:14px; color:#5b6474; min-height:18px; }
.pa-keys{ display:grid; grid-template-columns:repeat(3,76px); gap:14px 26px; justify-content:center; margin-top:6px; }
.pa-key{ all:unset; box-sizing:border-box; cursor:pointer; width:76px; height:76px; border-radius:50%; background:#eef1f5; display:grid; place-content:center; text-align:center; user-select:none; -webkit-touch-callout:none; touch-action:manipulation; }
.pa-key b{ font-size:30px; font-weight:500; line-height:1; color:var(--ink); }
.pa-key small{ font-size:10px; font-weight:700; letter-spacing:1.5px; color:#7b8494; }
.pa-key:active{ background:#d9dee6; }
.pa-key:focus-visible,.pa-round:focus-visible{ outline:2px solid var(--gold); outline-offset:2px; }
.pa-sms{ justify-items:stretch; gap:10px; }
.pa-sms .pa-phone-status{ justify-content:center; }
.pa-sms-to{ all:unset; box-sizing:border-box; width:100%; padding:12px 14px; border-radius:14px; background:#eef1f5; font-size:17px; font-weight:600; color:var(--ink); }
.pa-sms-row{ display:flex; gap:10px; align-items:flex-end; }
.pa-sms-row textarea{ flex:1; min-width:0; box-sizing:border-box; resize:none; padding:12px 14px; border:0; border-radius:18px; background:#eef1f5; font:inherit; font-size:16px; color:var(--ink); }
.pa-sms-row textarea:focus-visible,.pa-sms-to:focus-visible{ outline:2px solid var(--gold); outline-offset:1px; }
.pa-send{ width:48px; height:48px; font-size:22px; font-weight:700; }
.pa-phone-actions{ display:grid; grid-template-columns:repeat(3,76px); gap:26px; justify-content:center; align-items:center; margin-top:12px; }
.pa-round{ all:unset; box-sizing:border-box; cursor:pointer; width:76px; height:76px; border-radius:50%; display:grid; place-items:center; font-size:26px; background:rgba(255,255,255,.16); color:#fff; }
.pa-round:disabled{ opacity:.4; cursor:not-allowed; }
.pa-round[aria-pressed="true"]{ background:#fff; color:var(--navy); }
.pa-green{ background:#12b76a; color:#fff; }
.pa-red{ background:#f04438; color:#fff; }
.pa-red svg{ transform:rotate(135deg); }
.pa-plain{ width:56px; height:56px; justify-self:center; background:none; color:#5b6474; font-size:24px; }
.pa-call{ position:absolute; inset:0; z-index:40; display:flex; flex-direction:column; align-items:center; justify-content:space-between; gap:18px; padding:56px 20px calc(36px + env(safe-area-inset-bottom)); color:#fff; background:linear-gradient(180deg,#24395a 0%,var(--navy) 60%,#0b1a30 100%); animation:tbFade .2s ease; }
.pa-call-who{ display:grid; justify-items:center; gap:6px; text-align:center; }
.pa-call-who h2{ margin:10px 0 0; font-size:28px; font-weight:650; color:#fff; }
.pa-call-who p{ margin:4px 0 0; font-size:16px; color:rgba(255,255,255,.75); font-variant-numeric:tabular-nums; }
.pa-call-avatar{ width:96px; height:96px; border-radius:50%; display:grid; place-items:center; font-size:40px; font-weight:600; background:rgba(255,255,255,.14); }
.pa-call-ctrls,.pa-call-two{ display:grid; grid-template-columns:repeat(3,auto); gap:26px; }
.pa-call-two{ grid-template-columns:repeat(2,auto); gap:72px; }
.pa-call-ctrls span,.pa-call-two span{ display:grid; justify-items:center; gap:8px; font-size:13px; color:rgba(255,255,255,.85); }
.pa-call .pa-key{ background:rgba(255,255,255,.14); }
.pa-call .pa-key b{ color:#fff; }
.pa-call .pa-key small{ color:rgba(255,255,255,.6); }
.pa-call-end{ display:grid; justify-items:center; gap:14px; }
.pa-call-link{ color:#fff !important; }
.pa-call .tb-lang{ max-width:240px; }
.pa-callpill{ all:unset; box-sizing:border-box; cursor:pointer; position:absolute; top:10px; left:50%; transform:translateX(-50%); z-index:40; display:flex; align-items:center; gap:8px; max-width:calc(100% - 32px); padding:8px 16px; border-radius:999px; background:#12b76a; color:#fff; font-size:14px; font-weight:650; box-shadow:0 6px 18px rgba(18,183,106,.35); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.pa-callpill svg{ width:18px; height:18px; flex:0 0 auto; }
@media (prefers-reduced-motion: reduce){ .pa-sheet,.pa-sheet-bg,.pa-call{ animation:none; } }
/* wide screens: more breathing room inside the wider column (last, so it wins) */
@media (min-width:700px){
  .pa-pad{ padding:24px 32px 32px; gap:20px; }
  .pa-askwrap{ margin-inline:32px; }
  .pa-hero{ padding-inline:32px; }
  .pa-title{ padding-inline:32px; }
  .pa-seg{ margin-inline:32px; }
  .pa-bar{ padding-inline:32px; }
  .pa-milo .tb-chat{ padding-inline:32px; }
  .pa-composer{ padding-inline:28px; }
  .pa-days{ margin-inline:-32px; padding-inline:32px; scroll-padding-inline:32px; }
  .pa-day{ flex-basis:240px; }
}

.pa .tb-choice[aria-pressed="true"]{ background:var(--mint); border-color:var(--green); box-shadow:0 0 0 1px var(--green); }
/* Milo look: soft greens, Manrope headings, DM Sans text (last, so it wins) */
.pa{ --ink:#173e34; --navy:#285d47; --green:#285d47; --muted:#78847b; --faint:#9aa59c; --line:#e4e8de; --mint:#e4eedf; --peach:#f7e7d8; --yellow:#f6ebc0;
  --cream:#fff; --gold:#285d47; --gold-ink:#285d47; --beige:#e4eedf; --brown:#173e34; --blue:#285d47; --blue-bg:#e4eedf;
  --pa-bg:#f7f8f2; --pa-muted:#6d7a71; background:#e9eee5; font-family:"DM Sans",-apple-system,"Segoe UI","PingFang SC",sans-serif; color:var(--ink); }
.pa :is(h1,h2,h3,.pa-big,.pa-h,.pa-brand,.pa-hero2 strong){ font-family:Manrope,"DM Sans",sans-serif; }
.pa .pa-big{ font-size:30px; font-weight:750; letter-spacing:-.02em; }
.pa .pa-h{ font-size:18px; font-weight:700; }
.pa .tb-gold,.pa .tb-btn{ background:var(--green); color:#fff; box-shadow:none; }
.pa .tb-ghost{ background:var(--mint); color:var(--ink); }
.pa .tb-chip{ border-color:#cbd8c6; background:rgba(255,255,255,.6); }
.pa .tb-input:focus{ border-color:var(--green); box-shadow:0 0 0 3px rgba(40,93,71,.15); }
.pa-kicker{ display:flex; align-items:center; gap:6px; font-size:12px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:var(--pa-muted); margin-bottom:6px; }
.pa-top{ flex:0 0 auto; display:flex; align-items:center; gap:10px; padding:14px 18px 6px; background:var(--pa-bg); }
.pa-logo{ display:grid; place-items:center; width:36px; height:36px; border-radius:11px; background:var(--green); color:#f0dc91; }
.pa-brand{ font-size:22px; font-weight:800; letter-spacing:-.02em; color:var(--ink); }
.pa-top .tb-bellwrap{ margin-inline-start:auto; }
.pa-top .tb-bell{ width:42px; height:42px; background:#fff; border:1px solid var(--line); color:var(--ink); }
.pa-nav{ background:#fff; }
.pa-nav button{ color:#8d988f; }
.pa-nav button[aria-current="page"],.pa-nav button[aria-current="page"] svg{ color:var(--green); }
.pa-pill{ display:inline-flex; align-items:center; gap:6px; align-self:flex-start; padding:6px 12px; border-radius:999px; background:#fff; color:var(--ink); font-size:14px; font-weight:600; white-space:nowrap; max-width:100%; overflow:hidden; text-overflow:ellipsis; }
.pa-hero2{ all:unset; box-sizing:border-box; cursor:pointer; position:relative; overflow:hidden; display:flex; flex-direction:column; gap:8px; min-height:200px; padding:18px 150px 18px 18px; border-radius:24px; background:var(--mint); border:1px solid #d6e3d0; }
.pa-hero2 strong{ font-size:24px; font-weight:750; line-height:1.15; letter-spacing:-.02em; color:var(--ink); margin-top:6px; }
.pa-hero2-sub{ font-size:15px; color:var(--pa-muted); }
.pa-hero2 small{ margin-top:auto; font-size:14px; font-weight:600; color:var(--ink); }
.pa-hero2:focus-visible{ outline:2px solid var(--green); outline-offset:2px; }
.pa-sky{ position:absolute; right:0; bottom:0; width:150px; height:auto; }
[dir="rtl"] .pa-hero2{ padding:18px 18px 18px 150px; }
[dir="rtl"] .pa-sky{ right:auto; left:0; transform:scaleX(-1); }
.pa-steps{ display:grid; gap:10px; }
.pa-step{ all:unset; box-sizing:border-box; cursor:pointer; display:flex; align-items:center; gap:14px; padding:14px 16px; border-radius:20px; background:#fff; border:1px solid var(--line); box-shadow:0 8px 30px rgba(23,62,52,.03); }
.pa-step > span:nth-child(2){ flex:1; min-width:0; }
.pa-step b{ display:block; font-size:16.5px; font-weight:650; }
.pa-step small{ display:block; font-size:14px; color:var(--pa-muted); margin-top:2px; }
.pa-step:focus-visible,.pa-tile:focus-visible{ outline:2px solid var(--green); outline-offset:2px; }
.pa-badge{ flex:0 0 46px; height:46px; border-radius:14px; display:grid; place-items:center; font-size:22px; background:var(--mint); }
.pa-badge.yellow{ background:var(--yellow); }
.pa-badge.peach{ background:var(--peach); }
.pa-tiles{ display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.pa-tile{ all:unset; box-sizing:border-box; cursor:pointer; display:grid; align-content:start; gap:4px; min-height:124px; padding:16px; border-radius:20px; background:#fff; border:1px solid var(--line); }
.pa-tile.peach{ background:var(--peach); border-color:#efd9c4; }
.pa-tile > span{ font-size:24px; margin-bottom:6px; }
.pa-tile b{ font-size:16px; font-weight:650; line-height:1.25; }
.pa-tile small{ font-size:13.5px; color:var(--pa-muted); }
.pa-actions{ flex:0 0 auto; display:flex; gap:8px; padding:10px 14px; background:var(--pa-bg); border-top:1px solid var(--line); }
.pa-actions button{ all:unset; box-sizing:border-box; cursor:pointer; flex:1; display:flex; align-items:center; justify-content:center; gap:8px; min-height:50px; padding:0 10px; border-radius:16px; background:#fff; border:1px solid var(--line); font-size:15px; font-weight:600; color:var(--ink); white-space:nowrap; }
.pa-actions button.go{ flex:1.3; background:var(--green); border-color:var(--green); color:#fff; }
.pa-actions button:focus-visible{ outline:2px solid var(--green); outline-offset:2px; }
.pa-control{ display:flex; gap:12px; align-items:flex-start; padding:16px; border-radius:20px; background:var(--peach); font-size:22px; }
.pa-control b{ display:block; font-size:15px; }
.pa-control small{ display:block; font-size:15px; font-weight:600; color:var(--ink); }
.pa .pa-pass{ background:var(--mint); color:var(--ink); border:1px solid #d6e3d0; }
.pa .pa-pass .pa-big{ color:var(--ink); }
.pa .pa-pass span{ color:var(--pa-muted); }
.pa .pa-credits{ background:#fff; }
.pa .pa-credits small{ color:var(--pa-muted); }
.pa .pa-credits strong{ color:var(--ink); }
.pa .pa-includes li{ color:var(--ink); }
.pa .pa-includes li::before{ color:var(--green); }
.pa .pa-pass .pa-strip i{ background:rgba(40,93,71,.15); }
.pa .pa-pass .pa-strip i.done{ background:rgba(40,93,71,.4); }
.pa .pa-strip i.now{ background:var(--green); box-shadow:none; }
.pa-seg{ background:transparent; padding:0; gap:8px; }
.pa-seg button{ flex:0 0 auto; padding:0 16px; border-radius:999px; border:1px solid var(--line); background:#fff; }
.pa-seg button[aria-selected="true"]{ background:var(--green); color:#fff; border-color:var(--green); box-shadow:none; }
.pa-meter i{ background:var(--green); }
.pa-row:hover{ background:#f4f7f1; }
.pa-head button{ color:var(--green); }
.pa .pa-hello{ font-size:16.5px; font-weight:500; line-height:1.5; padding:14px 16px; border-radius:20px; background:#fff; border:1px solid var(--line); }
@media (min-width:700px){ .pa-top{ padding-inline:32px; } }
`;

/** Bottom sheet over the app; Esc or the backdrop closes it. */
function Sheet({ title, onClose, children }) {
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="pa-sheet-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pa-sheet" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={ref}>
        <div className="pa-grab" aria-hidden="true" />
        <div className="pa-sheet-head">
          <h3>{title}</h3>
          <button className="pa-x" aria-label={t('pa.close')} onClick={onClose}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** One segment per trip day: past days filled, today in gold. */
function TripStrip({ trip }) {
  const day = tripDay(trip);
  if (!day || day.kind !== 'day' || day.total > 21) return null;
  return (
    <div className="pa-strip" role="img" aria-label={tripDayText(day)}>
      {Array.from({ length: day.total }, (_, i) => <i key={i} className={i + 1 < day.n ? 'done' : i + 1 === day.n ? 'now' : ''} />)}
    </div>
  );
}

const esimNumbers = (state) => {
  const esim = state.esim || {};
  const total = (esim.gb || ESIM_PLAN.gb) + (esim.topupGb || 0);
  const used = Math.min(total, esim.usedGb || 0);
  return { esim, total, used, left: Math.round((total - used) * 10) / 10, live: ['ready', 'installed', 'active'].includes(esim.status) };
};

/** Data and Travel Number at a glance; both open the Data & Travel Number sheet. */
function StatusCards({ state, open }) {
  const { esim, total, used, left, live } = esimNumbers(state);
  const { number } = state;
  return (
    <div className="pa-status">
      <button className="pa-stat" onClick={() => open('esim')}>
        <small>{t('pa.data')}</small>
        <strong>{live ? t('pa.dataLeft', { gb: left }) : t('pa.dataSetup')}</strong>
        {live ? <div className="pa-meter"><i style={{ width: `${total ? ((total - used) / total) * 100 : 0}%` }} /></div> : <small>{t(`esim.status.${esim.status || 'none'}`)}</small>}
      </button>
      <button className="pa-stat" onClick={() => open('number')}>
        <small>{t('pa.phone')}</small>
        <strong>{number ? (number.status === 'active' ? t('pa.active') : t('shop.status.verify')) : t('pa.phoneSetup')}</strong>
        <small>{number ? <Ltr>{number.number}</Ltr> : t('today.stayReachable')}</small>
      </button>
    </div>
  );
}

/** Newest hotel message or finished search, with its trip update. */
function LatestNotice({ feed, go }) {
  const latest = feed.items?.find((c) => ((c.channel === 'sms' && c.sms.notification.sender !== 'traveler') || (c.channel === 'search' && c.search.status === 'done')) && Date.now() - new Date(c.createdAt) < 3 * 86400000);
  if (!latest) return null;
  const n = feedLine(latest);
  return (
    <div className="tb-notice" style={{ margin: 0, padding: 16, borderRadius: 20 }}>
      <small style={{ fontSize: 14 }}>{n.icon} {latest.channel === 'search' ? t('search.ready') : latest.sms.notification.business ? t('today.newFrom', { name: latest.sms.notification.business }) : t('today.newMessage')}</small>
      <strong style={{ fontSize: 18 }}>{n.title}</strong>
      <TripUpdate c={latest} />
      <button className="tb-link" style={{ marginTop: 8, fontSize: 15 }} onClick={() => (latest.channel === 'sms' ? go('phone', 'texts') : go('milo'))}>{t('notify.openAll')}</button>
    </div>
  );
}

/** Small steps for right now: what to set up or where to go next, from the trip as it stands. */
function nextSteps(state, { ask, open }) {
  const trip = state.trip;
  const next = nextStop(state.itinerary);
  const steps = [];
  if (!trip.destination) steps.push({ icon: '✈️', tone: 'mint', title: t('pa.setupTrip'), sub: t('today.whereTo'), on: () => open('trip', { sample: true }) });
  if (!esimNumbers(state).live) steps.push({ icon: '📶', tone: 'yellow', title: t('pa2.connect'), sub: t('pa2.connectSub'), on: () => open('esim') });
  if (next) steps.push({ icon: '🧭', tone: 'mint', title: next.title, sub: [friendlyDate(next.date), next.time, next.place].filter(Boolean).join(' · '), on: () => ask(t('pa.s.getTo', { place: next.place || next.title })) });
  else if (trip.hotelName) steps.push({ icon: '🏨', tone: 'mint', title: t('pa2.toHotel'), sub: t('pa2.toHotelSub', { hotel: trip.hotelName }), on: () => ask(t('pa.s.getTo', { place: trip.hotelName })) });
  if (trip.hotelName) steps.push({ icon: '🛎', tone: 'peach', title: t('pa2.tellHotel'), sub: t('pa2.tellHotelSub'), on: () => open('hotel') });
  return steps.slice(0, 3);
}

const Spark = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M12 3c.6 4.6 2.4 6.4 7 7-4.6.6-6.4 2.4-7 7-.6-4.6-2.4-6.4-7-7 4.6-.6 6.4-2.4 7-7z" /></svg>
);

/** Soft city skyline for the trip card (decorative, the same for every city). */
const Skyline = () => (
  <svg className="pa-sky" viewBox="0 0 160 120" aria-hidden="true">
    <circle cx="128" cy="30" r="17" fill="#f0dc91" />
    <rect x="18" y="62" width="22" height="50" rx="3" fill="#b9cdb4" />
    <rect x="44" y="40" width="18" height="72" rx="3" fill="#9fbc9a" />
    <path d="M86 112 96 28h4l10 84z" fill="#e9b9a0" />
    <rect x="94" y="56" width="12" height="3" fill="#fff" opacity=".7" />
    <rect x="114" y="70" width="26" height="42" rx="3" fill="#b9cdb4" />
    <rect x="64" y="84" width="22" height="28" rx="3" fill="#7fa37c" />
    <rect x="0" y="110" width="160" height="10" fill="#9fbc9a" />
  </svg>
);

/** The app's top bar: the Milo mark and notifications. */
function TopBar({ feed }) {
  return (
    <header className="pa-top">
      <span className="pa-logo"><Spark /></span>
      <b className="pa-brand">milo</b>
      <NotificationBell feed={feed} />
    </header>
  );
}

function TodayScreen({ app, ask, go, open, chat }) {
  const { state } = app;
  const trip = state.trip;
  const city = (trip.destination || '').split(/[,&]/)[0].trim();
  const name = trip.travelerName ? trip.travelerName.split(' ')[0] : app.account?.firstName || '';
  const day = tripDay(trip);
  const steps = nextSteps(state, { ask, open });
  const speak = () => {
    go('milo');
    if (!chat.mic.listening) chat.mic.toggle();
  };
  const tiles = [
    ['💳', 'peach', 'pay', () => ask(t('pa2.payAsk'))],
    ['🛎', 'mint', 'hotel', () => open('hotel')],
    ['🍜', 'mint', 'food', () => (trip.area || city ? ask(t('pa.s.lunch', { area: trip.area || city })) : go('milo', null, t('pa2.food')))],
    ['🧭', 'mint', 'wrong', () => ask(t('pa2.wrongAsk'))],
  ];

  return (
    <>
      <div className="pa-screen">
        <div className="pa-pad">
          <div>
            <div className="pa-kicker">{t('pa2.kicker')}</div>
            <h1 className="pa-big">{name ? t('pa2.welcome', { name }) : t('pa2.welcomeNoName')}</h1>
            <p className="tb-note" style={{ margin: '6px 0 0' }}>{t('pa2.welcomeSub')}</p>
          </div>

          <button className="pa-hero2" onClick={() => (trip.destination ? go('trips') : open('trip', { sample: true }))}>
            <span className="pa-pill">📍 {trip.area || city || t('pa.noTrip')}</span>
            <strong>{city ? t('pa2.here', { city }) : t('pa.setupTrip')}</strong>
            <span className="pa-hero2-sub">{city ? t('pa2.nextBit') : t('today.whereTo')}</span>
            {day?.kind && <small>☀ {tripDayText(day)}</small>}
            <Skyline />
          </button>

          <LatestNotice feed={app.feed} go={go} />

          {steps.length > 0 && (
            <>
              <h3 className="pa-h">{t('pa2.steps')}</h3>
              <div className="pa-steps">
                {steps.map((st) => (
                  <button key={st.title} className="pa-step" onClick={st.on}>
                    <span className={`pa-badge ${st.tone}`} aria-hidden="true">{st.icon}</span>
                    <span><b>{st.title}</b><small>{st.sub}</small></span>
                    <Chevron />
                  </button>
                ))}
              </div>
            </>
          )}

          <h3 className="pa-h">{t('pa2.help')}</h3>
          <div className="pa-tiles">
            {tiles.map(([icon, tone, k, on]) => (
              <button key={k} className={`pa-tile ${tone}`} onClick={on}>
                <span aria-hidden="true">{icon}</span>
                <b>{t(`pa2.${k}`)}</b>
                <small>{t(`pa2.${k}Sub`)}</small>
              </button>
            ))}
          </div>
          <button className="pa-step" onClick={() => open('bookings')}>
            <span className="pa-badge mint" aria-hidden="true">🧾</span>
            <span><b>{t('pa2.essentials')}</b><small>{t('pa2.essentialsSub')}</small></span>
            <Chevron />
          </button>

          <StatusCards state={state} open={open} />
        </div>
      </div>
      <div className="pa-actions">
        <button onClick={() => open('translate')}>📷 {t('pa2.camera')}</button>
        {chat.mic.supported && <button onClick={speak}>🎙 {t('pa2.speak')}</button>}
        <button className="go" onClick={() => go('milo', null, ' ')}><Spark size={16} /> {t('pa2.askMilo')}</button>
      </div>
    </>
  );
}

function MiloScreen({ app, chat, open }) {
  const listRef = useRef(null);
  const { state } = app;
  const city = (state.trip.destination || '').split(/[,&]/)[0].trim();
  const next = nextStop(state.itinerary);
  const events = chatEvents(app.feed.items || [], chat);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [chat.messages, chat.busy, events.length]);

  const tripAsks = [
    next && t('pa.s.getTo', { place: next.place || next.title }),
    t('pa.s.adjust'),
    t('pa.s.lunch', { area: state.trip.area || city || '' }),
    state.trip.hotelName && t('pa.s.callHotel'),
  ].filter(Boolean);
  const popular = [t('pa.s.flight'), t('pa.s.nearby'), t('pa.s.ready'), t('pa.s.connect'), t('pa.s.calls'), t('pa.s.translate')];
  const Rows = ({ items }) => (
    <div className="pa-rows">{items.map((q) => <button key={q} className="pa-row" onClick={() => chat.send(q)}><span>{q}</span><Chevron /></button>)}</div>
  );

  return (
    <div className="pa-milo">
      {chat.messages.length === 0 && events.length === 0 ? (
        <div className="pa-screen">
          <div className="pa-pad">
            <div className="pa-title-row">
              <div>
                <h1 className="pa-big">{t('pa2.withYou')}</h1>
                <p className="tb-note" style={{ margin: '6px 0 0' }}>{t('pa2.withYouSub')}</p>
              </div>
              {city && <span className="pa-pill">📍 {city}</span>}
            </div>
            <div className="pa-kicker"><Spark size={14} /> milo</div>
            <p className="pa-hello">{t('pa.hello')}</p>
            {city && <h3 className="pa-h">{t('pa.forTrip', { city })}</h3>}
            {city && <Rows items={tripAsks} />}
            <h3 className="pa-h">{t('pa.popular')}</h3>
            <Rows items={popular} />
            <div className="pa-control">
              <span aria-hidden="true">🛡</span>
              <span><b>{t('pa2.control')}</b><small>{t('pa2.controlSub')}</small></span>
            </div>
          </div>
        </div>
      ) : (
        <ChatThread chat={chat} state={state} listRef={listRef} events={events} />
      )}
      <div className="pa-composer">
        <div className="pa-attach" role="group" aria-label={t('pa.attach')}>
          <button className="tb-chip" onClick={() => open('translate')}>📷 {t('pa.a.translate')}</button>
          <button className="tb-chip" onClick={() => open('food')}>🍜 {t('pa.a.food')}</button>
          <button className="tb-chip" onClick={() => open('bookings')}>🧾 {t('pa.a.booking')}</button>
          {chat.messages.length > 0 && <button className="tb-chip" onClick={chat.reset}>✨ {t('chat.new')}</button>}
        </div>
        <ChatComposer chat={chat} id="pa-chat-in" />
        {chat.error && (
          <div className="tb-err" role="alert">
            {chat.error}
            {chat.noCredits && <button className="tb-btn" style={{ marginTop: 8, display: 'flex' }} onClick={() => open('credits')}>{t('pa.buyCredits')}</button>}
          </div>
        )}
      </div>
    </div>
  );
}

const TRIP_TABS = ['overview', 'itinerary', 'all'];

function BookingRows({ list, open, empty }) {
  return (
    <div className="pa-rows">
      {list.length ? list.map((b, i) => (
        <button key={i} className={`pa-row${b.status === 'cancelled' ? ' off' : ''}`} onClick={() => open('bookings')}>
          <span><b>{b.name}</b><small>{[b.start && formatDateTime(b.start), b.confirmation].filter(Boolean).join(', ')}</small></span>
          <span className={`pa-tag${b.source === 'milo-flights' ? ' mid' : ''}`}>{t(`bookingType.${b.type}`) === `bookingType.${b.type}` ? b.type : t(`bookingType.${b.type}`)}</span>
        </button>
      )) : <div className="pa-row" style={{ cursor: 'default' }}><span className="tb-note">{empty}</span></div>}
    </div>
  );
}

function TripsScreen({ app, tab, setTab, open, ask, go }) {
  const { state, feed } = app;
  const trip = state.trip;
  const next = nextStop(state.itinerary);
  const today = new Date().toLocaleDateString('en-CA');
  const todays = state.itinerary?.days?.find((d) => d.date === today);
  const bookings = [...(state.bookings || [])].sort((a, b) => String(a.start).localeCompare(String(b.start)));
  const confirmed = bookings.filter(isConfirmedBooking);
  const pending = bookings.filter((b) => !isConfirmedBooking(b));
  const recent = (feed.items || []).filter((c) => c.channel === 'call' || (c.channel === 'sms' && c.sms.notification.sender !== 'traveler')).slice(0, 3);
  const dates = trip.startDate ? `${formatDate(trip.startDate, { month: 'short', day: 'numeric' })} – ${formatDate(trip.endDate || trip.startDate, { month: 'short', day: 'numeric' })}` : '';

  return (
    <div className="pa-screen">
      <div className="pa-title">
        {dates && <div className="pa-kicker">{dates}</div>}
        <h1 className="pa-big">{trip.destination ? t('pa2.yourTrip', { city: trip.destination.split(/[,&]/)[0].trim() }) : t('pa.noTrip')}</h1>
      </div>
      <div className="pa-seg" role="tablist">
        {TRIP_TABS.map((k) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{t(`pa.t.${k}`)}</button>)}
      </div>
      {tab === 'overview' && (
        <div className="pa-pad">
          <button className="pa-pass" onClick={() => setTab('itinerary')}>
            <span>{t('pa.itinerarySub')}</span>
            <strong className="pa-big">{next ? next.title : t('pa.t.itinerary')}</strong>
            {next && <span>{[friendlyDate(next.date), next.time, next.place].filter(Boolean).join(', ')}</span>}
            <TripStrip trip={trip} />
          </button>

          <div className="pa-head"><h3 className="pa-h">{t('pa.todaysPlan')}</h3><button onClick={() => setTab('itinerary')}>{t('pa.seeAll')}</button></div>
          <div className="pa-rows">
            {todays?.items?.length ? todays.items.map((it, i) => (
              <button key={i} className="pa-row" onClick={() => ask(t('pa.s.getTo', { place: it.place || it.title }))}>
                <span><b><Ltr>{it.time}</Ltr>  {it.title}</b><small>{it.place}</small></span>
                <span aria-hidden="true">🧭</span>
              </button>
            )) : <div className="pa-row" style={{ cursor: 'default' }}><span className="tb-note">{trip.destination ? t('today.noPlan') : t('today.whereTo')}</span></div>}
          </div>

          <div className="pa-head"><h3 className="pa-h">{t('pa.confirmed')}</h3><button onClick={() => open('bookings')}>{t('pa.manage')}</button></div>
          <BookingRows list={confirmed} open={open} empty={t('pa.noBookings')} />
          {pending.length > 0 && (
            <>
              <div className="pa-head"><h3 className="pa-h">{t('pa.unconfirmed')}</h3></div>
              <BookingRows list={pending} open={open} />
            </>
          )}

          <div className="pa-head"><h3 className="pa-h">{t('pa.recentCalls')}</h3><button onClick={() => go('phone', 'calls')}>{t('pa.seeAll')}</button></div>
          <div className="pa-rows">
            {recent.length ? recent.map((c) => {
              const l = feedLine(c);
              return <button key={c.conversationId} className="pa-row" onClick={() => go('phone', c.channel === 'sms' ? 'texts' : 'calls')}><span><b>{l.who}</b><small>{l.title}</small></span><Chevron /></button>;
            }) : <div className="pa-row" style={{ cursor: 'default' }}><span className="tb-note">{t('results.empty')}</span></div>}
          </div>
        </div>
      )}
      {tab === 'itinerary' && <div className="pa-pad"><PlanSection ctx={app.ctx} state={state} update={app.update} /></div>}
      {tab === 'all' && <AllTrips app={app} setTab={setTab} open={open} />}
    </div>
  );
}

const DIAL_KEYS = [['1', ''], ['2', 'ABC'], ['3', 'DEF'], ['4', 'GHI'], ['5', 'JKL'], ['6', 'MNO'], ['7', 'PQRS'], ['8', 'TUV'], ['9', 'WXYZ'], ['*', ''], ['0', '+'], ['#', '']];
const HandsetIcon = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true"><path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1z" /></svg>
);

const CallIcon = ({ name }) => (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {{
      mute: <><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" /></>,
      keys: [5, 12, 19].flatMap((y) => [5, 12, 19].map((x) => <circle key={`${x}${y}`} cx={x} cy={y} r="1.3" fill="currentColor" stroke="none" />)),
      hide: <path d="M6 9.5l6 6 6-6" />,
    }[name]}
  </svg>
);

/** Phone-style dial pad; holding 0 types +. */
function DialPad({ onPress }) {
  const hold = useRef(null);
  const down = (k) => {
    if (k !== '0') return;
    hold.current = setTimeout(() => { hold.current = 'plus'; onPress('+'); }, 500);
  };
  const up = (k) => {
    if (hold.current === 'plus') { hold.current = null; return; }
    clearTimeout(hold.current);
    hold.current = null;
    onPress(k);
  };
  return (
    <div className="pa-keys" dir="ltr">
      {DIAL_KEYS.map(([k, sub]) => (
        <button key={k} className="pa-key" onPointerDown={() => down(k)} onClick={() => up(k)}>
          <b>{k}</b><small>{sub || ' '}</small>
        </button>
      ))}
    </div>
  );
}

function PhonePad({ phone, number, setNumber, label, onText }) {
  return (
    <div className="pa-phone">
      <div className="pa-phone-status">
        <span className={`tb-dot ${phone.status}`} aria-hidden="true" />
        {phone.status === 'ready' ? t('dialer.ready', { number: phone.callerId }) : phone.status === 'connecting' ? t('dialer.starting') : t('dialer.micNote')}
      </div>
      <input className="pa-dial" aria-label={t('dialer.number')} value={number} dir="ltr" inputMode="tel" placeholder="+81 75 …" onChange={(e) => setNumber(e.target.value)} />
      <div className="pa-dial-label">{label || ' '}</div>
      <DialPad onPress={(k) => setNumber((n) => n + k)} />
      <div className="pa-phone-actions">
        <button className="pa-round pa-plain" aria-label={t('pa.sms.text')} onClick={onText}>💬</button>
        <button className="pa-round pa-green" aria-label={t('dialer.call')} disabled={phone.status !== 'ready' || !!phone.call || !number.trim()} onClick={() => phone.dial(number.trim(), label)}><HandsetIcon /></button>
        {number ? <button className="pa-round pa-plain" aria-label="⌫" onClick={() => setNumber((n) => n.slice(0, -1))}>⌫</button> : <span />}
      </div>
      {!phone.on && <button className="tb-btn" onClick={phone.turnOn}>{t('dialer.start')}</button>}
      {phone.error && <div className="tb-err" role="alert">{phone.error}</div>}
    </div>
  );
}

/** Full-screen call, over any tab; Hide shrinks it to a bar at the top. */
function CallScreen({ phone }) {
  const [hidden, setHidden] = useState(false);
  const [keys, setKeys] = useState(false);
  const c = phone.call;
  const incoming = c.state === 'incoming';
  const status = incoming ? t('dialer.incoming') : c.state === 'active' ? mmss(phone.seconds) : c.state === 'ringing' ? t('dialer.ringing') : t('dialer.calling');
  if (hidden && !incoming) {
    return <button className="pa-callpill" onClick={() => setHidden(false)}><HandsetIcon /> {c.label || <Ltr>{c.number}</Ltr>} · {status}</button>;
  }
  return (
    <div className="pa-call" role="dialog" aria-modal="true" aria-label={status}>
      <div className="pa-call-who">
        <div className="pa-call-avatar" aria-hidden="true">{(c.label || '☎').slice(0, 1)}</div>
        <h2>{c.label || <Ltr>{c.number}</Ltr>}</h2>
        {c.label && <Ltr>{c.number}</Ltr>}
        <p>{status}</p>
      </div>
      {incoming ? (
        <div className="pa-call-end pa-call-two">
          <span><button className="pa-round pa-red" aria-label={t('dialer.decline')} onClick={phone.decline}><HandsetIcon /></button>{t('dialer.decline')}</span>
          <span><button className="pa-round pa-green" aria-label={t('dialer.answer')} onClick={phone.answer}><HandsetIcon /></button>{t('dialer.answer')}</span>
        </div>
      ) : (
        <>
          {keys ? <DialPad onPress={phone.sendDigits} /> : (
            <div className="pa-call-ctrls">
              <span><button className="pa-round" aria-label={t('dialer.mute')} aria-pressed={phone.muted} onClick={phone.toggleMute}><CallIcon name="mute" /></button>{phone.muted ? t('dialer.unmute') : t('dialer.mute')}</span>
              <span><button className="pa-round" aria-label={t('dialer.keypad')} onClick={() => setKeys(true)}><CallIcon name="keys" /></button>{t('dialer.keypad')}</span>
              <span><button className="pa-round" aria-label={t('pa.hideCall')} onClick={() => setHidden(true)}><CallIcon name="hide" /></button>{t('pa.hideCall')}</span>
            </div>
          )}
          {phone.outputs.length > 1 && (
            <select className="tb-lang" aria-label={t('dialer.speaker')} value={phone.outputId} onChange={(e) => phone.setOutput(e.target.value)}>
              {phone.outputs.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          )}
          <div className="pa-call-end">
            {keys && <button className="tb-link pa-call-link" onClick={() => setKeys(false)}>{t('pa.hideKeys')}</button>}
            <button className="pa-round pa-red" aria-label={t('dialer.hangUp')} onClick={phone.hangUp}><HandsetIcon /></button>
          </div>
        </>
      )}
    </div>
  );
}

const PHONE_TABS = [['keypad', 'dialer.keypad'], ['calls', 'pa.ph.calls'], ['texts', 'pa.ph.texts']];

/** Write a text from the app's own number; sent texts are kept on this device. */
function SmsCompose({ app, phone, to, setTo }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    const entry = { id: `${Date.now()}`, to: to.trim(), body: body.trim(), at: new Date().toISOString() };
    try {
      const r = await postJson(api('sms/send'), { travelerId: app.state.trip.travelerId, to: entry.to, body: entry.body, meta: app.meta });
      app.updateWith((s) => ({ textLog: [{ ...entry, to: r.to, status: r.status }, ...(s.textLog || [])].slice(0, 100) }));
      setBody('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="pa-phone pa-sms" onSubmit={send}>
      <div className="pa-phone-status">
        <span className={`tb-dot ${phone.status}`} aria-hidden="true" />
        {t('pa.sms.from', { number: app.state.number?.number || phone.callerId || '…' })}
      </div>
      <input className="pa-sms-to" aria-label={t('pa.sms.to')} placeholder={t('pa.sms.to')} value={to} dir="ltr" inputMode="tel" onChange={(e) => setTo(e.target.value)} />
      <div className="pa-sms-row">
        <textarea aria-label={t('pa.sms.ph')} placeholder={t('pa.sms.ph')} value={body} dir="auto" rows={2} maxLength={1000} onChange={(e) => setBody(e.target.value)} />
        <button type="submit" className="pa-round pa-green pa-send" aria-label={t('pa.sms.send')} disabled={busy || phone.status !== 'ready' || !to.trim() || !body.trim()}>{busy ? '…' : '↑'}</button>
      </div>
      {error && <div className="tb-err" role="alert">{t('pa.sms.failed')}: {error}</div>}
    </form>
  );
}

/** Texts on the travel number, both ways (↗ sent, ↙ received); tapping one replies to that number. */
function SentTexts({ log = [], onPick }) {
  if (!log.length) return <p className="tb-note">{t('pa.sms.none')}</p>;
  return (
    <div className="pa-rows">
      {log.map((m) => (
        <button key={m.id} className="pa-row" onClick={() => onPick(m.to)}>
          <span><b><span aria-label={t(m.direction === 'in' ? 'dialer.in' : 'dialer.out')}>{m.direction === 'in' ? '↙' : '↗'}</span> <Ltr>{m.to}</Ltr></b><small dir="auto">{m.body}</small></span>
          <small className="tb-note">{formatDateTime(m.at)}</small>
        </button>
      ))}
    </div>
  );
}

/** Call/SMS: the in-app phone on its own number, every call (yours and Milo's) and every text Milo received. */
function PhoneScreen({ app, phone, tab, setTab, open }) {
  const { state, feed } = app;
  const [number, setNumber] = useState('');
  const [picked, setPicked] = useState({ number: '', name: '' });
  const { on, turnOn } = phone;
  const hasNumber = Boolean(state.number?.number);
  useEffect(() => { if (!on && hasNumber) turnOn(); }, [on, turnOn, hasNumber]);
  const needNumber = (
    <section className="pa-pass pa-account">
      <strong className="pa-big">{t('pa.travelNumber')}</strong>
      <span>{t('pa.needNumber')}</span>
      <div className="pa-acct-btns"><button className="tb-gold" onClick={() => open('number')}>{t('pa.phoneSetup')}</button></div>
    </section>
  );
  const only = (channel) => ({ ...feed, items: feed.items && feed.items.filter((c) => c.channel === channel) });
  return (
    <div className="pa-screen">
      <div className="pa-title"><h1 className="pa-big">{t('pa.tab.phone')}</h1></div>
      <div className="pa-seg" role="tablist">
        {PHONE_TABS.map(([k, label]) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{t(label)}</button>)}
      </div>
      <div className="pa-pad">
        {tab === 'keypad' && (
          <>
            {hasNumber ? <PhonePad phone={phone} number={number} setNumber={setNumber} label={number === picked.number ? picked.name : ''} onText={() => setTab('texts')} /> : needNumber}
            <TripPlaces ctx={app.ctx} state={state} onPick={(n, name) => { setNumber(n); setPicked({ number: n, name }); }} />
          </>
        )}
        {tab === 'calls' && (
          <>
            <div><h3 className="pa-h">{t('dialer.history')}</h3><PhoneLog phone={phone} log={state.phoneLog} /></div>
            <MiloCallsSection feed={only('call')} />
          </>
        )}
        {tab === 'texts' && (
          <>
            {hasNumber ? <SmsCompose app={app} phone={phone} to={number} setTo={setNumber} /> : needNumber}
            <div><h3 className="pa-h">{t('pa.sms.yours')}</h3><SentTexts log={state.textLog} onPick={setNumber} /></div>
            <MiloCallsSection feed={only('sms')} />
          </>
        )}
      </div>
    </div>
  );
}

export const creditsOf = (state) => (Number.isFinite(state?.credits) ? state.credits : 0);

/** Credit packs paid by card (p3Billing.js), what each Milo action costs, and the ledger. */
function BuyCredits({ app }) {
  const { billing, reload, error: loadError } = useBilling(requestJson, api);
  const pay = usePay(requestJson, api);
  const [history, setHistory] = useState([]);
  const [pack, setPack] = useState(1000);
  const [card, setCard] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [added, setAdded] = useState(0);
  const updateRef = useRef(app.updateWith);
  updateRef.current = app.updateWith;

  const refresh = useCallback(async () => {
    const [b, h] = await Promise.all([reload(), requestJson(api('billing/transactions?limit=10'))]);
    setHistory(h.transactions);
    updateRef.current(() => ({ credits: b.credits }));
  }, [reload]);
  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  const buy = async () => {
    setBusy(true);
    setError('');
    setAdded(0);
    try {
      const r = await pay('billing/buy', { credits: pack }, card);
      if (r.paymentMethodId) setCard(r.paymentMethodId);
      setAdded(r.added);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      refresh().catch(() => {});
    }
  };
  const settings = (body) => requestJson(api('billing/settings'), { method: 'PUT', body }).then(refresh).catch((e) => setError(e.message));

  if (!billing) return loadError ? <div className="tb-err" role="alert">{loadError}</div> : <p className="tb-note">…</p>;
  const price = billing.packs.find((p) => p.credits === pack)?.cents || 0;
  const { costs } = billing;
  return (
    <div style={{ display: 'grid', gap: 14, padding: '0 4px 8px' }}>
      <p className="tb-note" style={{ margin: 0 }}>{t('pa.creditsSub')}</p>
      <div className="pa-li-big"><span>{t('pa.credits')}</span><strong>{formatNumber(billing.credits)}</strong></div>
      <div className="tb-sub" style={{ margin: 0 }}>{t('pay.costsTitle')}</div>
      <ul className="tb-note" style={{ margin: 0, paddingInlineStart: 18 }}>
        <li>{t('pay.costMessage', { n: formatNumber(costs.message) })}</li>
        <li>{t('pay.costSearch', { n: formatNumber(costs.search) })}</li>
        <li>{t('pay.costCall', { n: formatNumber(costs.callPerMinute) })}</li>
      </ul>
      {!payReady ? <p className="tb-note" style={{ margin: 0 }}>{t('pa.pay.notSetUp')}</p> : (
        <>
          <div className="tb-choices" role="group" aria-label={t('pa.buyCredits')}>
            {billing.packs.map((p) => (
              <button key={p.credits} className="tb-choice" aria-pressed={pack === p.credits} onClick={() => { setPack(p.credits); setAdded(0); }}>
                <strong className="tb-gb">{formatNumber(p.credits)}</strong>
                <small>{t('pa.credits')}</small>
                <span className="tb-price">{cents(p.cents)}</span>
              </button>
            ))}
          </div>
          <CardPicker billing={billing} card={card} setCard={setCard} onChanged={refresh} requestJson={requestJson} api={api} />
          <button className="tb-gold" onClick={buy} disabled={busy || !price}>
            {busy ? <><span className="tb-live" aria-hidden="true" />{t('shop.paying')}</> : t('pa.buyFor', { price: cents(price) })}
          </button>
          {error && <div className="tb-err" role="alert" style={{ margin: 0 }}>{error}</div>}
          {added > 0 && <div className="tb-ok" role="status" style={{ margin: 0 }}>✓ {t('pa.creditsAdded', { n: formatNumber(added) })}</div>}
          {billing.cards.length > 0 && (
            <label className="pa-acct-check">
              <input type="checkbox" checked={billing.autoRecharge} onChange={(e) => settings({ autoRecharge: e.target.checked, pack })} />
              <span>
                {t('pa.pay.auto', { n: formatNumber(billing.autoRecharge ? billing.pack : pack) })}{' '}
                <select value={billing.threshold} aria-label={t('pa.pay.auto', { n: '' })} onChange={(e) => settings({ threshold: Number(e.target.value) })}>
                  {billing.thresholds.map((n) => <option key={n} value={n}>{formatNumber(n)}</option>)}
                </select>
              </span>
            </label>
          )}
        </>
      )}
      {history.length > 0 && (
        <>
          <div className="tb-sub" style={{ margin: 0 }}>{t('pa.pay.history')}</div>
          {history.map((h) => (
            <div key={h.transactionId} className="pa-li-big" style={{ fontSize: 14 }}>
              <span>{formatDateTime(h.createdAt)}{h.description ? ` · ${h.description}` : h.action ? ` · ${h.action}` : ''}</span>
              <strong dir="ltr">{h.credits > 0 ? '+' : ''}{formatNumber(h.credits)}{h.amountCents ? ` · ${cents(h.amountCents)}` : ''}</strong>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

const PRODUCTS = [
  ['🧭', 'companion'], ['🐉', 'china'], ['✨', 'aipass'],
];

/** Other Xcards products, with the one the traveler owns marked. */
function Products() {
  return (
    <div className="pa-pad" style={{ padding: '0 4px 8px' }}>
      <p className="tb-note" style={{ margin: 0 }}>{t('pa.getMoreSub')}</p>
      <div className="pa-rows">
        {PRODUCTS.map(([icon, id]) => (
          <div key={id} className="pa-row" style={{ cursor: 'default' }}>
            <span aria-hidden="true" className="pa-prod-icon">{icon}</span>
            <span><b>{t(`pa.prod.${id}`)}</b><small>{t(`pa.prod.${id}.desc`)}</small></span>
          </div>
        ))}
        <div className="pa-row" style={{ cursor: 'default' }}>
          <span aria-hidden="true" className="pa-prod-icon">🎫</span>
          <span><b>{t('pa.travelPass')}</b><small>{t('pa.prod.travelpass.desc')}</small></span>
          <span className="pa-tag">{t('pa.owned')}</span>
        </div>
      </div>
    </div>
  );
}

const MY_TOOLS = [
  ['📶', 'esim'], ['📞', 'number'], ['🌐', 'translate'], ['🍜', 'food'],
  ['🧾', 'bookings'], ['🛎', 'hotel'], ['📍', 'local'], ['✅', 'ready'],
];

/** Who is signed in (opens the account sheet), or a prompt to create an account / log in. */
function AccountCard({ app, open }) {
  const { account, syncError } = app;
  if (!account) return null;
  return (
    <div className="pa-rows">
      <button className="pa-row" onClick={() => open('account')}>
        <span><b>{[account.firstName, account.lastName].filter(Boolean).join(' ') || <Ltr>{account.email}</Ltr>}</b><small><Ltr>{account.email}</Ltr></small></span>
        <span className={`pa-tag${syncError ? ' mid' : ''}`}>{syncError ? t('pa.acct.notSaved') : t('pa.acct.saved')}</span>
        <Chevron />
      </button>
    </div>
  );
}

/** Profile, texts opt-in, password, sign out and delete. */
function AccountSheet({ app }) {
  const { account, accountChanged, signOut, syncError } = app;
  const [form, setForm] = useState({ firstName: account?.firstName || '', lastName: account?.lastName || '', phone: account?.phone || '' });
  const [pw, setPw] = useState({ current: '', next: '' });
  const [deletePw, setDeletePw] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  if (!account) return null;

  const run = (key, fn) => async (e) => {
    e?.preventDefault();
    setBusy(key);
    setMsg(null);
    try {
      const text = await fn();
      if (text) setMsg({ ok: true, text });
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    } finally {
      setBusy('');
    }
  };
  const put = async (body) => {
    const { account: a } = await requestJson(api('account/me'), { method: 'PUT', body });
    accountChanged(a);
    return t('pa.acct.updated');
  };
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = run('profile', () => put(form));
  const texts = (on) => run('sms', () => put({ smsConsent: on, pageUrl: window.location.href }))();
  const twoStep = (on) => run('mfa', () => put(on ? { mfa: 'email' } : { mfa: 'none', currentPassword: pw.current }))();
  const changePw = run('pw', async () => {
    await requestJson(api('account/me'), { method: 'PUT', body: { currentPassword: pw.current, newPassword: pw.next } });
    setPw({ current: '', next: '' });
    return t('pa.acct.pwChanged');
  });
  const remove = run('delete', async () => {
    if (!window.confirm(t('pa.acct.deleteConfirm'))) return '';
    await requestJson(api('account/me'), { method: 'DELETE', body: { password: deletePw } });
    await signOut();
    return '';
  });

  return (
    <div className="pa-acct">
      {syncError && <div className="tb-err" role="alert">{t('pa.acct.syncFailed', { error: syncError })}</div>}
      <form onSubmit={save}>
        <Field id="acct-first" label={t('pa.acct.firstName')} value={form.firstName} onChange={set('firstName')} autoComplete="given-name" />
        <Field id="acct-last" label={t('pa.acct.lastName')} value={form.lastName} onChange={set('lastName')} autoComplete="family-name" />
        <Field id="acct-phone" label={t('pa.acct.phone')} value={form.phone} onChange={set('phone')} type="tel" inputMode="tel" dir="ltr" autoComplete="tel" />
        <button className="tb-btn" type="submit" disabled={busy === 'profile'}>{t('pa.acct.save')}</button>
      </form>

      <h3 className="pa-h">{t('pa.acct.twoStep')}</h3>
      <label className="pa-acct-check">
        <input type="checkbox" checked={account.mfa === 'email'} disabled={busy === 'mfa'} onChange={(e) => twoStep(e.target.checked)} />
        <span>{t('pa.acct.twoStepSub')}</span>
      </label>

      <h3 className="pa-h">{t('pa.acct.texts')}</h3>
      <label className="pa-acct-check">
        <input type="checkbox" checked={account.smsStatus === 'opted_in'} disabled={busy === 'sms'} onChange={(e) => texts(e.target.checked)} />
        <span lang="en">{SMS_CONSENT}</span>
      </label>

      <form onSubmit={changePw}>
        <h3 className="pa-h">{t('pa.acct.password')}</h3>
        <Field id="acct-pw" label={t('pa.acct.currentPw')} value={pw.current} onChange={(v) => setPw((p) => ({ ...p, current: v }))} type="password" autoComplete="current-password" />
        <Field id="acct-pw-new" label={t('pa.acct.newPw')} value={pw.next} onChange={(v) => setPw((p) => ({ ...p, next: v }))} type="password" autoComplete="new-password" />
        <button className="tb-ghost" type="submit" disabled={busy === 'pw' || !pw.current || pw.next.length < 8}>{t('pa.acct.changePw')}</button>
      </form>

      {msg && <div className={msg.ok ? 'tb-ok' : 'tb-err'} role={msg.ok ? 'status' : 'alert'}>{msg.text}</div>}

      <button className="tb-btn" onClick={() => signOut()}>{t('pa.acct.signOut')}</button>

      <form onSubmit={remove} className="pa-acct-danger">
        <h3 className="pa-h">{t('pa.acct.delete')}</h3>
        <p className="tb-note">{t('pa.acct.deleteSub')}</p>
        <Field id="acct-del" label={t('pa.acct.currentPw')} value={deletePw} onChange={setDeletePw} type="password" autoComplete="current-password" />
        <button className="tb-ghost" type="submit" disabled={busy === 'delete' || !deletePw}>{t('pa.acct.delete')}</button>
      </form>
    </div>
  );
}

/** First time in the app without a name or mobile: ask for them once. Skip or × also counts, so it never comes back. */
function Onboarding({ app }) {
  const { account, accountChanged } = app;
  const [form, setForm] = useState({ firstName: account.firstName || '', lastName: account.lastName || '', phone: account.phone || '', sms: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const finish = async (body) => {
    setBusy(true);
    setError('');
    try {
      const { account: a } = await requestJson(api('account/me'), { method: 'PUT', body: { ...body, onboarded: true } });
      accountChanged(a);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;
  const skip = useCallback(() => finishRef.current({}), []);
  const phone = form.phone.trim();
  const save = (e) => {
    e.preventDefault();
    finish({
      firstName: form.firstName,
      lastName: form.lastName,
      ...(phone ? { phone } : {}),
      ...(phone && form.sms ? { smsConsent: true, pageUrl: window.location.href } : {}),
    });
  };
  return (
    <Sheet title={t('pa.onboard.title')} onClose={skip}>
      <form className="pa-acct" onSubmit={save}>
        <p className="tb-note">{t('pa.onboard.sub')}</p>
        <Field id="ob-first" label={t('pa.acct.firstName')} value={form.firstName} onChange={set('firstName')} autoComplete="given-name" />
        <Field id="ob-last" label={t('pa.acct.lastName')} value={form.lastName} onChange={set('lastName')} autoComplete="family-name" />
        <Field id="ob-phone" label={t('pa.acct.phone')} value={form.phone} onChange={set('phone')} type="tel" inputMode="tel" dir="ltr" autoComplete="tel" placeholder="+1 415 555 0123" />
        <p className="tb-note">{t('pa.onboard.phoneHint')}</p>
        {phone && (
          <label className="pa-acct-check">
            <input type="checkbox" checked={form.sms} onChange={(e) => set('sms')(e.target.checked)} />
            <span lang="en">{SMS_CONSENT}</span>
          </label>
        )}
        {error && <div className="tb-err" role="alert">{error}</div>}
        <button className="tb-gold" type="submit" disabled={busy}>{t('pa.onboard.continue')}</button>
        <button className="tb-ghost" type="button" disabled={busy} onClick={skip}>{t('pa.onboard.skip')}</button>
      </form>
    </Sheet>
  );
}

/** Every trip on the account: saved (now, upcoming, no dates yet) and past. Open, start or delete one. */
function AllTrips({ app, setTab, open }) {
  const { account, trips, state } = app;
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  if (!account) return <div className="pa-pad"><p className="tb-note">{t('pa.trips.loading')}</p></div>;
  const act = (id, fn) => async () => {
    setBusy(id);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };
  const dates = (x) => (x.startDate
    ? `${formatDate(x.startDate, { month: 'short', day: 'numeric' })} – ${formatDate(x.endDate || x.startDate, { month: 'short', day: 'numeric', year: 'numeric' })}`
    : t('pa.trips.noDates'));
  const groups = [['saved', (trips || []).filter((x) => x.status !== 'past')], ['past', (trips || []).filter((x) => x.status === 'past')]];
  return (
    <div className="pa-pad">
      <button className="tb-gold" disabled={!!busy} onClick={act('new', async () => { await app.newTrip(); setTab('overview'); open('trip'); })}>＋ {t('pa.trips.new')}</button>
      {trips === null && <p className="tb-note">{t('pa.trips.loading')}</p>}
      {groups.map(([key, list]) => (
        <div key={key}>
          <div className="pa-head"><h3 className="pa-h">{t(`pa.trips.${key}`)}</h3></div>
          <div className="pa-rows">
            {list.length ? list.map((x) => {
              const name = x.destination || t('pa.noTrip');
              const isOpen = x.tripId === state.trip.tripId;
              return (
                <div key={x.tripId} className="pa-row" style={{ cursor: 'default' }}>
                  <button className="pa-trip-open" disabled={!!busy} aria-current={isOpen || undefined} onClick={act(x.tripId, async () => { if (!isOpen) await app.openTrip(x.tripId); setTab('overview'); })}>
                    <b>{name}</b><small>{dates(x)}</small>
                  </button>
                  <span className={`pa-tag${x.status === 'past' || x.status === 'draft' ? ' mid' : ''}`}>{isOpen ? t('pa.trips.open') : t(`pa.trips.status.${x.status}`)}</span>
                  <button className="pa-x" disabled={!!busy} aria-label={t('pa.trips.delete', { name })} onClick={act(`del${x.tripId}`, async () => { if (window.confirm(t('pa.trips.deleteConfirm', { name }))) await app.deleteTrip(x.tripId); })}>✕</button>
                </div>
              );
            }) : <div className="pa-row" style={{ cursor: 'default' }}><span className="tb-note">{t(`pa.trips.none.${key}`)}</span></div>}
          </div>
        </div>
      ))}
      {error && <div className="tb-err" role="alert">{error}</div>}
    </div>
  );
}

function MyScreen({ app, open, go }) {
  const { state, lang, changeLanguage } = app;
  const { total, left, live } = esimNumbers(state);
  const { number } = state;
  return (
    <div className="pa-screen">
      <div className="pa-title">
        <div>
          <div className="pa-kicker">{t('pa2.companion')}</div>
          <h1 className="pa-big">{app.account?.firstName ? t('pa2.hello', { name: app.account.firstName }) : t('pa2.helloNoName')}</h1>
          <p className="tb-note" style={{ margin: '6px 0 0' }}>{t('pa2.mySub')}</p>
        </div>
        <button className="tb-chip" style={{ marginTop: 12 }} onClick={() => open('products')}>✦ {t('pa.getMore')}</button>
      </div>
      <div className="pa-pad">
        <AccountCard app={app} open={open} />

        <section className="pa-pass pa-product" aria-label={t('pa.travelPass')}>
          <div className="pa-product-head">
            <strong className="pa-big">{t('pa.travelPass')}</strong>
            <span className="pa-tag">{t('pa.active')}</span>
          </div>
          <span>{[state.trip.destination, state.trip.startDate && formatDate(state.trip.startDate)].filter(Boolean).join(', ') || t('pa.noTrip')}</span>
          <div className="pa-credits">
            <div><small>{t('pa.credits')}</small><strong>{formatNumber(creditsOf(state))}</strong></div>
            <button className="tb-gold" onClick={() => open('credits')}>{t('pa.buyCredits')}</button>
          </div>
          <ul className="pa-includes">
            {['milo', 'data', 'number', 'calling'].map((k) => <li key={k}>{t(`pa.inc.${k}`)}</li>)}
          </ul>
        </section>

        <div className="pa-rows">
          <button className="pa-row" onClick={() => open('esim')}>
            <span><b>{t('title.esim')}</b><small>{live ? t('pa.total', { gb: total }) : t('pa.notSet')}</small></span>
            <span className={`pa-tag${live ? '' : ' mid'}`}>{live ? t('pa.dataLeft', { gb: left }) : t('pa.dataSetup')}</span>
          </button>
          <button className="pa-row" onClick={() => open('number')}>
            <span><b>{t('pa.travelNumber')}</b><small>{number ? <Ltr>{number.number}</Ltr> : t('pa.notSet')}</small></span>
            <span className={`pa-tag${number?.status === 'active' ? '' : ' mid'}`}>{number ? (number.status === 'active' ? t('pa.active') : t('shop.status.verify')) : t('pa.phoneSetup')}</span>
          </button>
          <div className="pa-row" style={{ cursor: 'default' }}>
            <span><b>{t('pa.miloNumber')}</b><small>{app.numbers?.milo ? <Ltr>{app.numbers.milo}</Ltr> : t('pa.miloNumberPending')}</small></span>
            <small className="tb-note">{t('pa.miloNumberSub')}</small>
          </div>
          <button className="pa-row" onClick={() => go('phone', 'calls')}>
            <span><b>{t('pa.callsMessages')}</b><small>{t('pa.callsSub')}</small></span><Chevron />
          </button>
          <button className="pa-row" onClick={() => open('trip')}>
            <span><b>{t('pa.profile')}</b><small>{t('pa.profileSub')}</small></span><Chevron />
          </button>
        </div>

        <div><h3 className="pa-h">{t('pa.allTools')}</h3><span className="tb-note">{t('pa.allToolsSub')}</span></div>
        <div className="pa-quick pa-tools">
          {MY_TOOLS.map(([icon, id]) => <button key={id} onClick={() => open(id)}><span aria-hidden="true">{icon}</span>{t(`title.${id}`)}</button>)}
        </div>

        <div className="pa-rows">
          <label className="pa-row" style={{ cursor: 'default' }}>
            <span><b>{t('pa.language')}</b></span>
            <select value={lang} onChange={(e) => changeLanguage(e.target.value)}>
              {LANGUAGES.map((l) => <option key={l.code} value={l.code} lang={l.code}>{l.native}</option>)}
            </select>
          </label>
          <a className="pa-row" href="#/p3-testbench">
            <span><b>{t('pa.testBench')}</b><small>{t('pa.testBenchSub')}</small></span><Chevron />
          </a>
          {app.account && (
            <button className="pa-row pa-danger" onClick={() => app.signOut()}>
              <span><b>{t('pa.acct.signOut')}</b><small><Ltr>{app.account.email}</Ltr></small></span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function P3App() {
  const app = useBenchState();
  const [tab, setTab] = useState('today');
  const [tripTab, setTripTab] = useState('overview');
  const [phoneTab, setPhoneTab] = useState('keypad');
  const phone = useP3Phone(app.state, app.meta, app.updateWith);
  const [sheet, setSheet] = useState(null);
  const [sampleOpen, setSampleOpen] = useState(false);
  const all = { ...app, sampleOpen, setSampleOpen };

  const open = (id, { sample = false } = {}) => {
    if (sample) setSampleOpen(true);
    setSheet(id);
  };
  const go = (to, sub = null, prefill = '') => {
    setSheet(null);
    setTab(to);
    if (sub) (to === 'phone' ? setPhoneTab : setTripTab)(sub);
    if (prefill) {
      if (prefill.trim()) chat.setText(prefill);
      setTimeout(() => document.getElementById('pa-chat-in')?.focus(), 50);
    }
  };
  const chat = useMiloChat(app.ctx, { onOpen: (id) => route(id), bench: app.bench });
  const ask = (q) => {
    go('milo');
    chat.send(q);
  };

  // Section ids from shared components (and Milo's "open") → this app's tabs and sheets.
  const route = (id) => {
    if (SHEETS[id]) { setSheet(id); return true; }
    if (id === 'plan') { go('trips', 'itinerary'); return true; }
    if (id === 'results' || id === 'call') { go('phone', 'calls'); return true; }
    if (id === 'dialer') { go('phone', 'keypad'); return true; }
    if (id === 'chat') { go('milo'); return true; }
    if (id === 'today') { go('today'); return true; }
    return id.startsWith('g-');
  };
  const routeRef = useRef(route);
  routeRef.current = route;
  useEffect(() => {
    setJumpOverride((id) => routeRef.current(id));
    return () => setJumpOverride(null);
  }, []);

  const Body = sheet && SHEETS[sheet]?.[1];
  return (
    <PayProvider>
    <Bench.Provider value={app.bench}>
      <div className="tb pa" dir={dir()} lang={app.lang}>
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Manrope:wght@600;700;800&display=swap" />
        <style>{CSS + PA_CSS}</style>
        <div className="pa-wrap">
          {tab !== 'phone' && <TopBar feed={app.feed} />}
          {tab === 'today' && <TodayScreen app={all} ask={ask} go={go} open={open} chat={chat} />}
          {tab === 'milo' && <MiloScreen app={all} chat={chat} open={open} />}
          {tab === 'phone' && <PhoneScreen app={all} phone={phone} tab={phoneTab} setTab={setPhoneTab} open={open} />}
          {tab === 'trips' && <TripsScreen app={all} tab={tripTab} setTab={setTripTab} open={open} ask={ask} go={go} />}
          {tab === 'my' && <MyScreen app={all} open={open} go={go} />}
          <nav className="pa-nav" aria-label={t('nav.sections')}>
            {TABS.map((id) => (
              <button key={id} aria-current={tab === id ? 'page' : undefined} onClick={() => go(id)}>
                <Icon name={id} />{t(`pa.tab.${id}`)}{id === 'phone' && phone.call && <span className="pa-live" aria-hidden="true" />}
              </button>
            ))}
          </nav>
          {phone.call && <CallScreen key={`${phone.call.direction}${phone.call.number}`} phone={phone} />}
          {app.account && !app.account.onboarded && <Onboarding app={all} />}
          {Body && (
            <Sheet title={t(SHEETS[sheet][0])} onClose={() => setSheet(null)}>
              <Body app={all} />
            </Sheet>
          )}
        </div>
      </div>
    </Bench.Provider>
    </PayProvider>
  );
}
